import { useRef, useCallback } from 'react';

/**
 * Shared audio beep hook for exercise feedback
 *
 * @param {Object} options
 * @param {number} options.frequency - Oscillator frequency in Hz (default: 880 for A5, skipping)
 * @param {number} options.volume - Volume 0-1 (default: 0.5)
 * @param {number} options.duration - Duration in seconds (default: 0.15)
 * @returns {Object} { playBeep, initAudioContext }
 */
export function useAudioBeep(options = {}) {
  const {
    frequency = 880,  // A5 note (default for jumprope)
    volume = 0.5,
    duration = 0.15
  } = options;

  const audioContextRef = useRef(null);

  // Initialize Web Audio API context
  const initAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    // Resume if suspended (required for user gesture on iOS/Safari)
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }
    return audioContextRef.current;
  }, []);

  // Play beep sound using Web Audio API
  const playBeep = useCallback((overrides = {}) => {
    try {
      const audioContext = initAudioContext();

      // Create oscillator for beep
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      // Configure beep sound
      oscillator.type = 'sine';
      const beepFrequency = overrides.frequency || frequency;
      const beepVolume = overrides.volume || volume;
      const beepDuration = overrides.duration || duration;

      oscillator.frequency.setValueAtTime(beepFrequency, audioContext.currentTime);

      // Set volume and fade out
      gainNode.gain.setValueAtTime(beepVolume, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + beepDuration);

      // Play the beep
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + beepDuration);
    } catch (err) {
      console.error('Audio error:', err);
    }
  }, [initAudioContext, frequency, volume, duration]);

  // Play test beep (louder and longer)
  const playTestBeep = useCallback(() => {
    playBeep({ volume: 0.8, duration: 0.3 });
  }, [playBeep]);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
  }, []);

  return {
    playBeep,
    playTestBeep,
    initAudioContext,
    cleanup
  };
}

// Preset configurations for different exercise types
export const AUDIO_PRESETS = {
  skipping: { frequency: 880, volume: 0.5, duration: 0.15 },   // A5 note
  pickleball: { frequency: 1047, volume: 0.5, duration: 0.12 } // C6 note (higher)
};
